import {
	DefaultError,
	InvalidateQueryFilters,
	Mutation,
	MutationCache,
	QueryCache,
	QueryClient,
	QueryKey,
} from '@tanstack/react-query'
import { ReactNode } from 'react'
import { toast } from 'react-toastify'
import { ServerError } from './fetch'

async function handleQueryInvalidation(
	queryClient: QueryClient,
	mutationParams: Params,
	{ invalidateQueries, invalidateOnError }: MutationMeta = {},
) {
	if (!invalidateQueries) return
	if (mutationParams.error && invalidateOnError === false) return

	if (Array.isArray(invalidateQueries)) {
		await Promise.allSettled(
			invalidateQueries.map(queryKey => queryClient.invalidateQueries({ queryKey })),
		)
	} else {
		const queryFilters = invalidateQueries(mutationParams)
		await Promise.allSettled(queryFilters.map(filter => queryClient.invalidateQueries(filter)))
	}
}

function handleMutationNotifications(
	{ variables, data, error, context }: Params,
	mutation: Mutation<unknown, unknown, unknown, unknown>,
) {
	if (error instanceof ServerError) {
		toast.error(error.message)
		return
	}

	if (!mutation.meta?.notifications) return

	const notifMsg = error
		? mutation.meta.notifications.error?.(error, variables, context)
		: mutation.meta.notifications.success?.(data, variables, context)
	if (!notifMsg) return
	if (error) {
		toast.error(notifMsg)
	} else {
		toast.success(notifMsg)
	}
}

function handleQueryNotifications(error: DefaultError) {
	if (error instanceof ServerError) {
		toast.error(error.message)
	}
}

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			retry: false,
		},
	},

	queryCache: new QueryCache({
		async onError(error) {
			handleQueryNotifications(error)
		},
	}),

	mutationCache: new MutationCache({
		async onSettled(data, error, variables, context, mutation) {
			await handleQueryInvalidation(
				queryClient,
				{ data, error, variables, context },
				mutation.meta,
			).catch()
			handleMutationNotifications({ data, error, variables, context }, mutation)
		},
	}),
})

type Params = {
	data: unknown
	error: unknown
	variables: unknown
	context: unknown
}

type MutationInvalidationConfig =
	| Array<QueryKey>
	| ((invalidationParams: Params) => Array<InvalidateQueryFilters>)

export interface MutationNotificationConfig<
	TData = unknown,
	TError = unknown,
	TVariables = unknown,
	TContext = unknown,
> {
	success?: (data: TData, variables: TVariables, context: TContext) => ReactNode | undefined
	error?: (error: TError, variables: TVariables, context: TContext) => ReactNode | undefined
}

interface MutationMeta extends Record<string, unknown> {
	invalidateQueries?: MutationInvalidationConfig
	invalidateOnError?: boolean
	notifications?: MutationNotificationConfig
}

declare module '@tanstack/react-query' {
	interface Register {
		mutationMeta: MutationMeta
	}
}
